import { useState } from 'react'
import { Plus, Target, Users, MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/layout/page-header'
import { SectionHelp } from '@/components/help/section-help'
import { FieldHint } from '@/components/field-hint'
import { AnimatedNumber } from '@/components/animated-number'
import { CampaignCard } from './campaign-card'
import { CreateCampaignWizard } from './create-campaign-wizard'
import { useCampaigns } from './use-campaigns'
import type { CampaignFilters, CampaignStatus } from './types'

type StatCardProps = {
  readonly label: string
  readonly value: number
  readonly icon: React.ComponentType<{ className?: string }>
  readonly format?: (value: number) => string
  readonly suffix?: string
  readonly hint?: string
}

function StatCard({ label, value, icon: Icon, format, suffix, hint }: StatCardProps) {
  return (
    <Card className="group relative flex-1 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-10 -top-10 h-24 w-24 rounded-full bg-[var(--color-cobalt)]/0 blur-2xl transition-all duration-300 group-hover:bg-[var(--color-cobalt)]/15"
      />
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <p className="eyebrow text-[var(--color-silver)]">{label}</p>
            {hint && <FieldHint label={`What is ${label}?`}>{hint}</FieldHint>}
          </div>
          <Icon className="h-4 w-4 text-[var(--color-cobalt-bright)]" />
        </div>
        <div className="mt-1 overflow-clip-y text-2xl font-bold tracking-tight text-[var(--color-silver-bright)]">
          <AnimatedNumber
            value={value}
            format={format ?? ((n) => Math.round(n).toLocaleString())}
            className="inline-block animate-digit-drop"
          />
          {suffix}
        </div>
      </CardContent>
    </Card>
  )
}

type FilterTab = CampaignStatus | 'all'

const TABS: Array<{ value: FilterTab; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'draft', label: 'Draft' },
  { value: 'deleted', label: 'Deleted' },
]

type CampaignsListProps = {
  readonly onViewDetails: (id: string) => void
}

export function CampaignsList({ onViewDetails }: CampaignsListProps) {
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [wizardOpen, setWizardOpen] = useState(false)

  const filters: CampaignFilters =
    activeTab === 'all' ? {} : { status: activeTab }

  const { data: rawCampaigns = [], isLoading } = useCampaigns(filters)
  const { data: allRawCampaigns = [] } = useCampaigns({})

  // "All" tab excludes deleted campaigns; "Deleted" tab shows only deleted
  const campaigns = activeTab === 'all' ? rawCampaigns.filter((c) => c.status !== 'deleted') : rawCampaigns
  const allCampaigns = allRawCampaigns.filter((c) => c.status !== 'deleted')

  // Summary stats (exclude deleted)
  const activeCampaigns = allCampaigns.filter((c) => c.status === 'active')
  const totalLeads = allCampaigns.reduce((sum, c) => sum + c.leads_count, 0)
  const avgReplyRate =
    allCampaigns.length > 0
      ? allCampaigns.reduce((sum, c) => {
          const rate = c.sent_count > 0 ? (c.replied_count / c.sent_count) * 100 : 0
          return sum + rate
        }, 0) / allCampaigns.length
      : 0

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Campaigns"
        subtitle="Create and manage outreach campaigns."
        actions={
          <div className="flex items-center gap-2">
            <SectionHelp sectionKey="campaigns" />
            <Button onClick={() => setWizardOpen(true)} data-tour="campaigns-new">
              <Plus className="h-4 w-4 mr-1" />
              New Campaign
            </Button>
          </div>
        }
      />

      {/* Summary stats */}
      <div className="flex flex-col gap-3 sm:flex-row" data-tour="campaigns-stats">
        <StatCard
          label="Active Campaigns"
          value={activeCampaigns.length}
          icon={Target}
          hint="How many of your campaigns are live and sending right now."
        />
        <StatCard
          label="Total Leads"
          value={totalLeads}
          icon={Users}
          hint="Total people assigned across all your campaigns, not counting deleted ones."
        />
        <StatCard
          label="Avg Reply Rate"
          value={avgReplyRate}
          icon={MessageSquare}
          format={(n) => n.toFixed(1)}
          suffix="%"
          hint="Average share of contacted leads who replied, across your campaigns. Shows 0 until messages send."
        />
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 border-b" data-tour="campaigns-filters">
        {TABS.map((tab) => {
          const count =
            tab.value === 'all'
              ? allCampaigns.length
              : allRawCampaigns.filter((c) => c.status === tab.value).length

          return (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors ${
                activeTab === tab.value
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <Badge
                  variant={activeTab === tab.value ? 'default' : 'secondary'}
                  className="text-xs h-4 px-1"
                >
                  {count}
                </Badge>
              )}
            </button>
          )
        })}
      </div>

      {/* Campaign cards grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 rounded-xl border bg-muted/30 animate-pulse" />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-lg font-medium">No campaigns</p>
          <p className="text-sm text-muted-foreground">
            {activeTab === 'all'
              ? "Create your first campaign to start automating outreach."
              : `No ${activeTab} campaigns.`}
          </p>
          {activeTab === 'all' && (
            <Button onClick={() => setWizardOpen(true)} className="mt-2">
              <Plus className="h-4 w-4 mr-1" />
              New Campaign
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-tour="campaigns-grid">
          {campaigns.map((campaign) => (
            <CampaignCard
              key={campaign.id}
              campaign={campaign}
              onViewDetails={onViewDetails}
            />
          ))}
        </div>
      )}

      <CreateCampaignWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onCreated={onViewDetails}
      />
    </div>
  )
}
